import { CPopover } from "@coreui/react";

import {
  DEBIT_NOTE_STATUS_APPROVED_BY_SET,
  DEBIT_NOTE_STATUS_CANCELLATION_FAILED,
  DEBIT_NOTE_STATUS_CANCELLATION_REQUESTED,
  DEBIT_NOTE_STATUS_CANCELLED,
  DEBIT_NOTE_STATUS_CHOICES,
  DEBIT_NOTE_STATUS_ERROR,
  DEBIT_NOTE_STATUS_RECOVERABLE_ERROR,
  DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  DEBIT_NOTE_STATUS_UPLOADED_TO_SET,
  DebitNote,
} from "../../models/debit-note";
import { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import DocumentError from "../../models/document-error";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";

interface DebitNoteCrudStatusProps {
  debitNote: DebitNote;
}

const DebitNoteCrudStatus: React.FC<DebitNoteCrudStatusProps> = ({
  debitNote,
}) => {
  const [currentDebitNote, setCurrentDebitNote] = useState(debitNote);

  useEffect(() => {
    setCurrentDebitNote(debitNote);
  }, [debitNote]);
  const [retrievedError, setRetrievedError] = useState<DocumentError>();
  const [loadingError, setLoadingError] = useState(false);

  useSub(`debit_note_${debitNote.id}`, (debitNote: DebitNote) => {
    setCurrentDebitNote(debitNote);
  });

  useEffect(() => {
    setCurrentDebitNote(debitNote);
  }, [debitNote]);

  useEffect(() => {
    const fetchError = async () => {
      const errorCode = currentDebitNote.setResponseCode;
      const errorMessage =
        currentDebitNote.setResponseDescription || currentDebitNote.setError;

      if (
        !(
          currentDebitNote.status === DEBIT_NOTE_STATUS_ERROR ||
          currentDebitNote.status === DEBIT_NOTE_STATUS_CANCELLATION_FAILED ||
          currentDebitNote.status === DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR
        )
      ) {
        setRetrievedError(undefined);
        return;
      }

      if (!errorCode && !errorMessage) {
        setRetrievedError(undefined);
        return;
      }

      setLoadingError(true);
      try {
        let result;
        if (errorCode) {
          const params = new Map<string, string | undefined>();
          params.set("error_code", errorCode);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (
          (!result || result.status !== SUCCESS || !result.data) &&
          errorMessage
        ) {
          const params = new Map<string, string | undefined>();
          params.set("raw_message", errorMessage);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (result?.status === SUCCESS && result.data) {
          setRetrievedError(result.data);
        } else {
          setRetrievedError(undefined);
        }
      } catch (err) {
        console.error("Error loading document error:", err);
        setRetrievedError(undefined);
      } finally {
        setLoadingError(false);
      }
    };

    fetchError();
  }, [currentDebitNote]);

  let badgeClassName = "badge-info";

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_UPLOADED_TO_SET) {
    badgeClassName = "badge-warning";
  }

  if (
    currentDebitNote.status === DEBIT_NOTE_STATUS_ERROR ||
    currentDebitNote.status === DEBIT_NOTE_STATUS_RECOVERABLE_ERROR
  ) {
    badgeClassName = "badge-danger";
  }

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_APPROVED_BY_SET) {
    badgeClassName = "badge-success";
  }

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_CANCELLATION_REQUESTED) {
    badgeClassName = "badge-light";
  }

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_CANCELLED) {
    badgeClassName = "badge-info";
  }

  if (currentDebitNote.status === DEBIT_NOTE_STATUS_CANCELLATION_FAILED) {
    badgeClassName = "badge-danger";
  }

  const renderPopoverContent = () => {
    if (
      ![
        DEBIT_NOTE_STATUS_ERROR,
        DEBIT_NOTE_STATUS_CANCELLATION_FAILED,
        DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
      ].includes(currentDebitNote.status!)
    )
      return null;

    return (
      <ul
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "0px",
          marginBottom: "0px",
        }}
      >
        {currentDebitNote.setResponseCode && (
          <li>Código: {currentDebitNote.setResponseCode}</li>
        )}
        {currentDebitNote.setResponseDescription && (
          <li>Descripción: {currentDebitNote.setResponseDescription}</li>
        )}
        {currentDebitNote.setError && (
          <li>Error: {currentDebitNote.setError}</li>
        )}
        {!loadingError && retrievedError && (
          <>
            {retrievedError.description && (
              <li>Significado: {retrievedError.description}</li>
            )}
            {retrievedError.possibleSolutions && (
              <li>Posible solución: {retrievedError.possibleSolutions}</li>
            )}
          </>
        )}
      </ul>
    );
  };

  return (
    <td className="text-center">
      <span
        className={`badge badge-pill ${badgeClassName}`}
        style={{ fontSize: "14px" }}
      >
        {DEBIT_NOTE_STATUS_CHOICES.get(currentDebitNote.status!)}{" "}
        {renderPopoverContent() ? (
          <CPopover
            content={renderPopoverContent()}
            placement="top"
            trigger="click"
          >
            <span
              className="fa fa-info-circle"
              style={{ cursor: "pointer" }}
            ></span>
          </CPopover>
        ) : null}
      </span>
    </td>
  );
};

export default DebitNoteCrudStatus;
